import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { ConditionNode, LogicalNode, Node } from "app/types/record-management-types";
import SysColumn from "app/class/sys-column";
import useUniqueTable from "app/hooks/useUniqueTable";
import useTableColumns from "app/hooks/useTableColumns";
import queryKeys from "shared/services/queryKeys";

const FALLBACK_DISPLAY_COLUMN_NAMES = [
    "number_ref",
    "name",
    "label",
    "title",
    "subject",
    "code",
    "email",
];

const FALLBACK_TEXT_COLUMN_TYPES = new Set([
    "TEXT",
    "CHOICE",
    "TABLE_NAME",
    "RICH_HTML",
    "CODE",
]);

export const getReferenceDisplayColumnName = (columns: SysColumn[]) => {
    const activeColumns = columns.filter((column) => column.active && column.name);
    const displayColumn = activeColumns.find((column: SysColumn) => column.is_display);

    if (displayColumn?.name) {
        return displayColumn.name;
    }

    const namedFallbackColumn = FALLBACK_DISPLAY_COLUMN_NAMES
        .map((name) => activeColumns.find((column) => column.name === name))
        .find(Boolean);

    if (namedFallbackColumn?.name) {
        return namedFallbackColumn.name;
    }

    const textColumn = activeColumns.find((column) => FALLBACK_TEXT_COLUMN_TYPES.has(column.column_type));

    return textColumn?.name || "id";
};

export const useReferenceDisplayColumn = (referenceTableName?: string) => {
    const table = useUniqueTable(referenceTableName || "");
    const columns = useTableColumns(table?.id || "");

    const displayColumnName = useMemo(() => {
        return getReferenceDisplayColumnName(columns);
    }, [columns]);

    return {
        displayColumnName,
        columns,
    };
};

export const normalizeReferenceRecord = (record: any, selectedId?: string | null) => {
    if (!record) {
        return null;
    }

    if (record?.data && typeof record.data === "object") {
        return {
            id: record.id ?? selectedId ?? null,
            ...record.data,
        };
    }

    return record;
};

interface UseReferenceOptionsParams {
    tableName?: string;
    selectedId?: string | null;
    search?: string;
    displayColumnName?: string;
    evaluatedFilters?: LogicalNode | Record<string, any> | null;
    loadOptions?: boolean;
}

const isQueryNode = (value: unknown): value is Node =>
    Boolean(
        value &&
        typeof value === "object" &&
        ["condition", "logical", "exists"].includes(String((value as { type?: unknown }).type || ""))
    );

const normalizeEvaluatedFilters = (
    evaluatedFilters?: LogicalNode | Record<string, any> | null
): Node | null => {
    if (!evaluatedFilters) {
        return null;
    }

    if (isQueryNode(evaluatedFilters)) {
        return evaluatedFilters;
    }

    const conditions = Object.entries(evaluatedFilters)
        .filter(([field, value]) => Boolean(field) && value !== undefined)
        .map(([field, value]) => ({
            type: "condition",
            field,
            operator: "eq",
            value,
        } as ConditionNode));

    if (conditions.length === 0) {
        return null;
    }

    return {
        type: "logical",
        operator: "and",
        conditions,
    };
};

const buildReferenceSearchQuery = (
    evaluatedFilters: LogicalNode | Record<string, any> | null | undefined,
    displayColumnName?: string,
    search?: string
): LogicalNode | null => {
    const conditions: Node[] = [];
    const baseFilter = normalizeEvaluatedFilters(evaluatedFilters);

    if (baseFilter) {
        conditions.push(baseFilter);
    }

    if (search && displayColumnName) {
        conditions.push({
            type: "condition",
            field: displayColumnName,
            operator: "stw",
            value: search,
        });
    }

    if (conditions.length === 0) {
        return null;
    }

    return {
        type: "logical",
        operator: "and",
        conditions,
    };
};

export const useReferenceOptions = ({
    tableName,
    selectedId,
    search = "",
    displayColumnName,
    evaluatedFilters = null,
    loadOptions = true,
}: UseReferenceOptionsParams) => {
    const optionsQuery = useQuery({
        queryKey: queryKeys.records.lookup(
            tableName || "",
            "search",
            displayColumnName || "",
            search || "__initial__",
            JSON.stringify(evaluatedFilters)
        ),
        enabled: Boolean(loadOptions && tableName && displayColumnName),
        queryFn: async () => {
            const rm = new RecordManagement(tableName || "");
            const packageLookup = tableName === "platform_packages";
            const query = buildReferenceSearchQuery(evaluatedFilters, displayColumnName, packageLookup ? "" : search);

            if (query) {
                rm.builderFromQuery(query);
            }

            if (tableName === "sys_columns") rm.orderBy("order", "asc");
            rm.paginate(1, tableName === "sys_columns" || packageLookup ? 5000 : 30);

            const result = await rm.run();
            if (!packageLookup || !search) return result.data;
            const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
            const needle = normalize(search);
            return result.data.filter((row: any) => normalize(String(row[displayColumnName || "name"] || "")).includes(needle));
        },
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const selectedOptionQuery = useQuery({
        queryKey: queryKeys.records.lookup(tableName || "", "selected", selectedId || ""),
        enabled: Boolean(tableName && selectedId),
        queryFn: async () => {
            const record = await new RecordManagement(tableName || "").get(selectedId || "");
            return normalizeReferenceRecord(record, selectedId);
        },
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const data = useMemo(() => {
        const options = optionsQuery.data ?? [];
        const selectedOption = selectedOptionQuery.data;

        if (!selectedOption) {
            return options;
        }

        if (options.some((option: any) => option.id === selectedOption.id)) {
            return options;
        }

        return [selectedOption, ...options];
    }, [optionsQuery.data, selectedOptionQuery.data]);

    return {
        data,
        isLoading: optionsQuery.isLoading || selectedOptionQuery.isLoading,
    };
};
